import { useMemo, useState } from "react";
import {
  INTERRUPTION_LABELS,
  type CalibrationMetrics,
  type InterruptionReason,
  type QualityClass,
  type StudySettings,
} from "@gazelab/shared";
import { classifyValidation, meetsQuality } from "@gazelab/analysis";
import { readEnvironment, runChecks } from "../../../features/experiment/preflight.js";
import { Actions, ErrorText, Panel, fmt, stimulusWords } from "../ui.js";
import { errorMessage } from "../../../services/api.js";

function useBusy() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run, setError };
}

export function PreflightScreen({
  simulation,
  resuming,
  onStartCamera,
  onCancel,
}: {
  simulation: boolean;
  resuming: boolean;
  onStartCamera: () => Promise<void>;
  onCancel: () => void;
}) {
  const checks = useMemo(() => runChecks(readEnvironment()), []);
  const blocked = !simulation && checks.some((c) => c.blocking && !c.ok);
  const { busy, error, run } = useBusy();
  return (
    <Panel title={resuming ? "Reactivar la cámara" : "Comprobación del equipo"} wide>
      <ul className="space-y-2" data-testid="preflight-checks">
        {checks.map((c) => (
          <li key={c.id} className="flex items-start gap-2 text-sm">
            <span aria-hidden className={c.ok ? "text-emerald-700" : c.blocking ? "text-rose-700" : "text-amber-700"}>
              {c.ok ? "✔" : c.blocking ? "✖" : "!"}
            </span>
            <span>
              {c.label}
              {!c.ok && c.detail && <span className="block text-slate-500">{c.detail}</span>}
            </span>
          </li>
        ))}
      </ul>
      {simulation ? (
        <p className="text-sm">En modo simulación no se usa la cámara: los datos se generan artificialmente y quedan marcados como sintéticos.</p>
      ) : (
        <p className="text-sm">
          Al pulsar «Activar cámara», el navegador te pedirá permiso. Después verás tu imagen con un recuadro: colócate para
          que el recuadro se ponga verde. Asegúrate de que solo tu cara esté frente a la cámara.
        </p>
      )}
      {blocked && <ErrorText>Este equipo no cumple los requisitos mínimos para usar la cámara.</ErrorText>}
      {error && <ErrorText>{error}</ErrorText>}
      <Actions>
        <button type="button" className="btn-primary" disabled={busy || blocked} onClick={() => run(onStartCamera)} data-testid="preflight-continue">
          {busy ? "Iniciando…" : simulation ? "Continuar (simulación)" : "Activar cámara"}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
          Cancelar
        </button>
      </Actions>
    </Panel>
  );
}

export function FullscreenScreen({
  kind,
  simulation,
  preparing,
  onEnter,
  onSkipTestMode,
  onCancel,
}: {
  kind: "image" | "video" | "mixed";
  simulation: boolean;
  preparing: boolean;
  onEnter: () => Promise<void>;
  onSkipTestMode: () => Promise<void>;
  onCancel: () => void;
}) {
  const { busy, error, run } = useBusy();
  const working = busy || preparing;
  return (
    <Panel title="Pantalla completa">
      <p>
        {kind === "video" ? "Los videos se muestran" : kind === "mixed" ? "Las imágenes y los videos se muestran" : "Las fotografías se muestran"} a pantalla completa. No salgas de ella
        ni cambies de ventana hasta terminar.
      </p>
      <p className="text-sm text-slate-500">Si sales de pantalla completa, la captura se detendrá y habrá que volver a calibrar.</p>
      {error && <ErrorText>{error}</ErrorText>}
      <Actions>
        <button type="button" className="btn-primary" disabled={working} onClick={() => run(onEnter)} data-testid="fullscreen-enter">
          {working ? "Preparando…" : "Entrar a pantalla completa"}
        </button>
        {simulation && error && (
          <button type="button" className="btn-secondary" disabled={working} onClick={() => run(onSkipTestMode)} data-testid="fullscreen-skip-test">
            Continuar sin pantalla completa (modo de prueba)
          </button>
        )}
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={working}>
          Cancelar
        </button>
      </Actions>
    </Panel>
  );
}

/** Avance de la descarga de estímulos. */
export interface PreloadState {
  done: number;
  total: number;
  loadedBytes: number;
  totalBytes: number;
  ready: boolean;
  error: string | null;
}

function PreloadStatus({ preload, noun }: { preload: PreloadState; noun: string }) {
  if (preload.error) return <ErrorText>No se pudieron preparar {noun}: {preload.error}</ErrorText>;
  if (preload.ready) return null;
  const pct = preload.totalBytes > 0 ? Math.min(100, Math.round((preload.loadedBytes / preload.totalBytes) * 100)) : null;
  return (
    <div role="status" className="space-y-1 text-sm" data-testid="preload-status">
      <p>
        Descargando {noun}… {preload.done} / {preload.total}
        {pct !== null && ` (${pct} %)`}
      </p>
      {pct !== null && (
        <div className="h-2 overflow-hidden rounded-full bg-slate-200" aria-hidden>
          <div className="h-full rounded-full bg-sky-600 transition-[width]" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

export function CalibrationScreen({
  recalibrating,
  onRun,
  waitFor,
}: {
  recalibrating: boolean;
  onRun: () => Promise<void>;
  /** Si se indica, la calibración no empieza hasta terminar la descarga (estudios de video). */
  waitFor?: PreloadState | null;
}) {
  const { busy, error, run } = useBusy();
  const waiting = Boolean(waitFor && !waitFor.ready);
  return (
    <Panel title={recalibrating ? "Nueva calibración" : "Calibración"}>
      <p>
        Aparecerán <strong>9 puntos</strong>, uno a la vez, en distintas partes de la pantalla. <strong>Mira cada punto</strong> y haz
        clic sobre él mientras lo miras.
      </p>
      <p className="text-sm text-slate-500">Mantén la cabeza quieta durante todo el proceso.</p>
      {waitFor && <PreloadStatus preload={waitFor} noun="los videos" />}
      {waiting && !waitFor?.error && <p className="text-sm text-slate-500">La calibración se habilitará cuando terminen de descargarse los videos.</p>}
      {error && <ErrorText>{error}</ErrorText>}
      <Actions>
        <button type="button" className="btn-primary" disabled={busy || waiting} onClick={() => run(onRun)} data-testid="calibration-start">
          Comenzar calibración
        </button>
      </Actions>
    </Panel>
  );
}

export interface ValidationOutcome {
  metrics: CalibrationMetrics;
  startedAt: string;
  endedAt: string;
}

export type ValidationDecisionResult = { ok: true } | { ok: false; message: string; classification?: QualityClass };

export function ValidationScreen({
  phase,
  settings,
  onRun,
  onDecision,
}: {
  phase: "initial" | "revalidation";
  settings: StudySettings;
  onRun: () => Promise<ValidationOutcome>;
  onDecision: (outcome: ValidationOutcome, decision: "continue" | "recalibrate" | "override" | "cancel", reason?: string) => Promise<ValidationDecisionResult>;
}) {
  const [outcome, setOutcome] = useState<ValidationOutcome | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [serverMsg, setServerMsg] = useState<string | null>(null);
  const { busy, error, run } = useBusy();

  if (!outcome) {
    return (
      <Panel title={phase === "revalidation" ? "Comprobación rápida" : "Validación"}>
        <p>
          Ahora verás {settings.validationPoints} puntos. <strong>Míralos fijamente sin hacer clic</strong>; cada uno desaparece solo.
          Así medimos qué tan bien funciona la estimación.
        </p>
        {error && <ErrorText>{error}</ErrorText>}
        <Actions>
          <button type="button" className="btn-primary" disabled={busy} onClick={() => run(async () => setOutcome(await onRun()))} data-testid="validation-start">
            Comenzar validación
          </button>
        </Actions>
      </Panel>
    );
  }

  const m = outcome.metrics;
  const classification = classifyValidation(m, settings.quality, Math.min(5, settings.validationPoints));
  const acceptable = meetsQuality(classification, settings.minCalibrationQuality);
  const decide = (decision: "continue" | "recalibrate" | "override" | "cancel", why?: string) =>
    run(async () => {
      setServerMsg(null);
      const res = await onDecision(outcome, decision, why);
      if (!res.ok) setServerMsg(res.message);
    });

  const label: Record<QualityClass, string> = { pass: "Adecuada", warn: "Aceptable con reservas", fail: "Insuficiente" };
  return (
    <Panel title="Resultado de la validación" wide>
      <p data-testid="validation-classification" data-classification={classification}>
        Calidad estimada: <strong>{label[classification]}</strong>
      </p>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4" data-testid="validation-metrics">
        <dt className="text-slate-500">Error medio</dt>
        <dd>{fmt(m.meanErrorPx, 0, " px")}</dd>
        <dt className="text-slate-500">Error mediano</dt>
        <dd>{fmt(m.medianErrorPx, 0, " px")}</dd>
        <dt className="text-slate-500">Dentro del radio ({m.roiRadiusPx} px)</dt>
        <dd>{fmt(m.percentInRoi, 0, " %")}</dd>
        <dt className="text-slate-500">Muestras por segundo</dt>
        <dd>{fmt(m.samplesPerSecond, 1)}</dd>
        <dt className="text-slate-500">Puntos evaluados</dt>
        <dd>
          {m.pointsEvaluated} / {m.points.length}
        </dd>
        <dt className="text-slate-500">Error relativo</dt>
        <dd>{fmt(m.meanErrorRelative === null ? null : m.meanErrorRelative * 100, 1, " % de la diagonal")}</dd>
      </dl>
      {!acceptable && (
        <p className="text-sm">
          La calidad no alcanza el mínimo del estudio. Lo recomendable es recalibrar (mejora la iluminación y mantén la cabeza quieta).
        </p>
      )}
      {serverMsg && <ErrorText>{serverMsg}</ErrorText>}
      {error && <ErrorText>{error}</ErrorText>}
      {overrideOpen && (
        <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
          <p>
            Continuar con baja calidad es una <strong>excepción que autoriza la persona investigadora</strong>: requiere que haya
            iniciado sesión en el panel en este mismo navegador. Queda registrada con su usuario y el motivo.
          </p>
          <label className="label" htmlFor="override-reason">
            Motivo de la excepción
          </label>
          <textarea
            id="override-reason"
            className="input"
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="override-reason"
          />
          <button
            type="button"
            className="btn-secondary"
            disabled={busy || reason.trim().length < 3}
            onClick={() => decide("override", reason.trim())}
            data-testid="override-confirm"
          >
            Autorizar y continuar
          </button>
        </div>
      )}
      <Actions>
        {acceptable && (
          <button type="button" className="btn-primary" disabled={busy} onClick={() => decide("continue")} data-testid="validation-continue">
            Continuar
          </button>
        )}
        <button type="button" className={acceptable ? "btn-secondary" : "btn-primary"} disabled={busy} onClick={() => decide("recalibrate")} data-testid="validation-recalibrate">
          Recalibrar
        </button>
        {!acceptable && settings.allowManualOverride && !overrideOpen && (
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => setOverrideOpen(true)} data-testid="validation-override">
            Continuar con baja calidad
          </button>
        )}
        <button type="button" className="btn-secondary" disabled={busy} onClick={() => decide("cancel")} data-testid="validation-cancel">
          Cancelar
        </button>
      </Actions>
    </Panel>
  );
}

export function ReadyScreen({
  preload,
  kind,
  audioCheck,
  onBegin,
}: {
  preload: PreloadState;
  kind: "image" | "video" | "mixed";
  /** Exigir una prueba de sonido antes de empezar (videos con audio). */
  audioCheck: boolean;
  onBegin: () => void;
}) {
  const [heard, setHeard] = useState(false);
  const w = stimulusWords(kind);
  return (
    <Panel title="Todo listo">
      <p className="text-lg" data-testid="ready-instructions">
        Observa libremente {w.each}
        {kind === "video" ? " hasta que termine" : kind === "mixed" ? " (los videos, hasta que terminen)" : ""}; evita mover la cabeza y mantén la mirada en la pantalla.
      </p>
      <p className="text-sm text-slate-500">
        Antes de {w.each} verás una cruz en el centro. No necesitas pulsar nada mientras ves {w.plural}. Si necesitas una pausa, pulsa la tecla{" "}
        <kbd className="rounded border px-1">P</kbd>: se hará al terminar {w.current}.
      </p>
      <PreloadStatus preload={preload} noun={kind === "video" ? "los videos" : kind === "mixed" ? "las imágenes y los videos" : "las imágenes"} />
      {audioCheck && <AudioCheck heard={heard} onHeard={() => setHeard(true)} />}
      <Actions>
        <button type="button" className="btn-primary" disabled={!preload.ready || (audioCheck && !heard)} onClick={onBegin} data-testid="ready-begin">
          Comenzar
        </button>
      </Actions>
    </Panel>
  );
}

/** Tono de prueba (generado en el navegador) para confirmar que el sonido funciona. */
function AudioCheck({ heard, onHeard }: { heard: boolean; onHeard: () => void }) {
  const [played, setPlayed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function play() {
    setError(null);
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 660;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 1.2);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 1.25);
      osc.onended = () => void ctx.close();
      setPlayed(true);
    } catch {
      setError("No se pudo reproducir el sonido de prueba en este navegador.");
    }
  }
  return (
    <div className="rounded-xl bg-sky-50 p-4 text-sm text-sky-950 ring-1 ring-sky-200" data-testid="audio-check">
      <p className="font-semibold">Prueba de sonido</p>
      <p className="mt-1">Los videos tienen sonido. Sube el volumen a un nivel cómodo y pulsa «Reproducir tono».</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" className="btn-secondary" onClick={play} data-testid="audio-play">
          Reproducir tono
        </button>
        <button type="button" className="btn-secondary" disabled={!played || heard} onClick={onHeard} data-testid="audio-heard">
          {heard ? "✔ Se escucha bien" : "Lo escuché"}
        </button>
      </div>
      {played && !heard && <p className="mt-2 text-xs">¿No escuchas nada? Revisa el volumen del equipo o los audífonos y vuelve a reproducirlo.</p>}
      {error && <p className="mt-2 text-xs text-rose-800">{error}</p>}
    </div>
  );
}

export function PauseScreen({ onResume }: { onResume: () => void }) {
  return (
    <Panel title="Pausa">
      <p>Descansa un momento sin mover demasiado la cabeza. Cuando quieras, continúa.</p>
      <Actions>
        <button type="button" className="btn-primary" onClick={onResume} data-testid="pause-resume">
          Continuar
        </button>
      </Actions>
    </Panel>
  );
}

export function InterruptedScreen({
  reason,
  kind,
  remaining,
  onResume,
  onFinish,
  onCancel,
}: {
  reason: InterruptionReason | null;
  kind: "image" | "video" | "mixed";
  remaining: number;
  onResume: () => void;
  onFinish: () => void;
  onCancel: () => void;
}) {
  return (
    <Panel title="Captura detenida">
      <p data-testid="interruption-reason" data-reason={reason ?? ""}>
        Motivo: <strong>{reason ? INTERRUPTION_LABELS[reason] : "desconocido"}</strong>.
      </p>
      <p>
        Por seguridad de los datos, {stimulusWords(kind).unused}.
        Para continuar hay que volver a pantalla completa y repetir la calibración y la validación.
      </p>
      <Actions>
        {remaining > 0 ? (
          <button type="button" className="btn-primary" onClick={onResume} data-testid="interrupted-resume">
            Reanudar ({remaining} {stimulusWords(kind).remaining(remaining)})
          </button>
        ) : (
          <button type="button" className="btn-primary" onClick={onFinish} data-testid="interrupted-finish">
            Finalizar
          </button>
        )}
        <button type="button" className="btn-secondary" onClick={onCancel} data-testid="interrupted-cancel">
          Cancelar participación
        </button>
      </Actions>
    </Panel>
  );
}
